<script setup>
import { ref, watchEffect, nextTick, computed } from 'vue';
import { useRuntimeConfig, useRequestURL } from '#app';
import { useBlocks } from '@/composables/useBlocks';
import { useSeo } from '@/composables/useSeo';
import { useSchemaOrg } from '@/composables/useSchemaOrg';
import { useUiStore, mapHeaderClass } from '@/stores/ui';

import { useGlobalStore } from '@/stores/global';

import Breadcrumbs from '@/components/ui/Breadcrumbs.vue';
import PageSectionTabsNav from '@/components/ui/PageSectionTabsNav.vue';
import BlockCarHero from '@/components/blocks/BlockCarHero.vue';
import CarText from '@/components/CarText.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isStock: {
    type: Boolean,
    default: false,
  },
});

const config = useRuntimeConfig();
const requestUrl = useRequestURL();
const clientUrl = (config.public.clientUrl || requestUrl.origin).replace(
  /\/+$/,
  '',
);

const globalStore = useGlobalStore();

onMounted(() => {
  if (props.data?.id) {
    globalStore.setCarId(props.data.id);
  }
});

onUnmounted(() => {
  globalStore.setCarId('');
});

watch(
  () => props.data?.id,
  (newId) => {
    globalStore.setCarId(newId || '');
  },
);

const blockComponents = useBlocks();
const ui = useUiStore();

watchEffect(() => {
  const c = props.data;
  if (!c) return;

  const fb = c?.sections?.[0];
  ui.setHeaderSkin(mapHeaderClass(fb?.data?.background_color));
});

const blockRefs = ref({});

const setBlockRef = (id) => (componentInstance) => {
  if (componentInstance) {
    if (componentInstance.$el) {
      blockRefs.value[id] = componentInstance.$el;
    } else if (componentInstance instanceof HTMLElement) {
      blockRefs.value[id] = componentInstance;
    } else {
      nextTick(() => {
        const element = document.querySelector(`[data-block-id="${id}"]`);
        if (element) {
          blockRefs.value[id] = element;
        }
      });
    }
  } else {
    delete blockRefs.value[id];
  }
};
useSeo({
  title: computed(() => props.data?.seo_title || props.data?.title || ''),
  description: computed(() => props.data?.meta_description || ''),
  keywords: computed(() => props.data?.meta_keywords || ''),
  robots: computed(() => props.data?.meta_robots || 'index, follow'),
  image: computed(() => props.data?.image_cover?.src || null),
  path: computed(() => props.data?.url || ''),
});

const carSchema = computed(() => {
  const carData = props.data;
  if (!carData || !carData.title) return null;

  const pageUrl = carData?.url
    ? carData.url.startsWith('http')
      ? carData.url
      : `${clientUrl}/${String(carData.url).replace(/^\/+/, '')}`
    : undefined;

  const imageUrl = carData?.image_cover?.src
    ? carData.image_cover.src.startsWith('http')
      ? carData.image_cover.src
      : `${clientUrl}${carData.image_cover.src}`
    : undefined;

  const hasPrice = !!carData.pricing?.price_current;

  return {
    '@context': 'https://schema.org',
    '@type': 'Vehicle',
    name: carData.title,
    ...(pageUrl && { url: pageUrl }),
    ...(carData?.brand_title && {
      brand: {
        '@type': 'Brand',
        name: carData.brand_title,
      },
    }),
    ...(carData?.model_title && { model: carData.model_title }),
    ...(carData?.year && { vehicleModelDate: carData.year }),
    ...(carData?.fuel && { fuelType: carData.fuel }),
    ...(carData?.transmission_title && { vehicleTransmission: carData.transmission_title }),
    offers: {
      '@type': 'Offer',
      ...(hasPrice && {
        price: String(carData.pricing.price_current),
        priceCurrency: 'UAH',
      }),
      availability:
        carData?.status_car === 'stock'
          ? 'https://schema.org/InStock'
          : 'https://schema.org/PreOrder',
      ...(pageUrl && { url: pageUrl }),
    },
    ...(imageUrl && { image: imageUrl }),
    ...(carData?.color_title && { color: carData.color_title }),
  };
});

useSchemaOrg(carSchema);

const lastSectionColor = computed(() => {
  const sections = props.data?.sections;
  if (!sections || !sections.length) return 'white';

  const finalBlockIndex = sections.findIndex(
    (block) => block.type === 'application',
  );
  const relevantSections =
    finalBlockIndex !== -1 ? sections.slice(0, finalBlockIndex) : sections;
  const lastBlock = relevantSections[relevantSections.length - 1];

  return lastBlock?.data?.background_color || 'white';
});
</script>

<template>
  <div class="page-dynamic">
    <Breadcrumbs
      v-if="data?.breadcrumbs?.items"
      :breadcrumbs="data.breadcrumbs.items"
    />

    <BlockCarHero
      :data="{
        background_color: 'white',
        title: data?.title,
        image: data?.image_cover,
        in_stock: data?.status,
        pricing: data?.pricing,
        link_full: data?.link_full,
        status: data?.status_car,
        financing: data?.financing,
      }"
    />
    <template v-for="(block, index) in data?.sections" :key="block.id">
      <PageSectionTabsNav
        v-if="data?.tabs?.length && index === 0"
        :tabs="data.tabs"
        :targets="blockRefs"
      />

      <component
        :is="blockComponents[block.type]"
        :id="block.id"
        :ref="setBlockRef(block.id)"
        :data="block.data"
        :page-type="data?.slug"
        :path="data?.slug"
        :is-stock="isStock"
        :is-car-page="true"
      />
    </template>
    <CarText
      v-if="data.intro"
      :text="data.intro"
      :background-color="lastSectionColor"
    />
  </div>
</template>
